import React, { useState } from 'react';
import { View } from 'react-native';
import moment from 'moment';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useQuery } from '@tanstack/react-query';
import MyDataTable from '../../../components/MyDataTable';
import MyAppText from '../../../components/MyAppText';
import Badge, { BadgeColor } from '../../../components/Badge';
import { Pagination } from '../../../components/Pagination';
import { translate } from '../../../services/translate';
import { accessService } from '../../../services/central-api/access';

interface Props {
    userId: number;
}

function getActionColor(action: string): BadgeColor {
    switch (action) {
        case 'created':
            return 'green';
        case 'updated':
            return 'blue';
        case 'blocked':
            return 'red';
        case 'unblocked':
            return 'teal';
        default:
            return 'gray';
    }
}

export default function UserHistoricList({ userId }: Props) {
    const { styles } = useStyles(styleSheet);

    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(25);

    const { data, isLoading } = useQuery({
        queryKey: ['getUserHistory', userId, page, limit],
        queryFn: () => accessService.getUserHistory({ userId, page, limit }),
        enabled: userId > 0,
    });

    function formatDetailValue(key: string, value: unknown) {
        if (/date/i.test(key) && value) {
            return moment(value as string | number | Date).format('DD/MM/YYYY HH:mm:ss');
        }

        if (typeof value === 'object') {
            return JSON.stringify(value);
        }

        return String(value);
    }

    const totalItems = (data ?? []).length === limit
        ? (page + 2) * limit
        : page * limit + (data ?? []).length;

    return (
        <View style={styles.container}>
            <MyDataTable
                columns={[
                    {
                        name: translate('date'),
                        selector: row => moment(row.date).format('DD/MM/YYYY HH:mm:ss'),
                        wrap: true,
                    },
                    {
                        name: translate('user'),
                        selector: row =>
                            row.userId === -1 && row.userName
                                ? translate(row.userName)
                                : row.userId
                                    ? `${row.userId} - ${row.userName}`
                                    : translate('notApplicable'),
                        wrap: true,
                    },
                    {
                        name: translate('ipAddress'),
                        selector: row => row.ip ?? '',
                    },
                    {
                        name: translate('action'),
                        cell: row => (
                            <View style={{ justifyContent: 'center' }}>
                                <Badge mode='SQUARE' size='medium' color={getActionColor(row.action)}>
                                    {translate(row.action)}
                                </Badge>
                            </View>
                        ),
                        wrap: true,
                    },
                ]}
                data={data ?? []}
                expandableRows
                expandableRowsComponent={({ data: log }) => (
                    <View style={styles.detailTable}>
                        {Object.entries(log.detail ?? {})
                            .filter(([k]) => k !== 'company' && k !== 'companyId')
                            .map(([k, v]) => (
                                <View key={k} style={styles.detailRow}>
                                    <MyAppText style={styles.detailKey}>{k}</MyAppText>
                                    <MyAppText style={styles.detailValue}>
                                        {formatDetailValue(k, v)}
                                    </MyAppText>
                                </View>
                            ))}
                    </View>
                )}
                progressPending={isLoading}
            />
            <Pagination
                totalItems={totalItems}
                currentPage={page}
                pageSize={limit}
                setPage={setPage}
                setPageSize={pageSize => {
                    setLimit(pageSize);
                    setPage(0);
                }}
                hideTotalItemsAndPagesInfo={true}
            />
        </View>
    );
}

const styleSheet = createStyleSheet(theme => ({
    container: {
        flex: 1,
        gap: 8,
    },
    detailTable: {
        backgroundColor: theme.colors.container.background,
        padding: 8,
        flex: 1,
    },
    detailRow: {
        flexDirection: 'row',
        paddingVertical: 4,
        borderBottomWidth: 1,
        borderColor: theme.colors.card.border,
    },
    detailKey: {
        flex: 1,
        fontSize: 12,
        fontWeight: '600',
    },
    detailValue: {
        flex: 2,
        fontSize: 12,
        maxWidth: 700,
    },
}));
